import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { SERVICES } from "../data/services";
import { MaskedLine, FadeUp } from "../components/Reveal";
import { ContactModal } from "../components/ContactModal";

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (service) {
      document.title = `${service.title} | JIYONOX`;
      document.querySelector('meta[name="description"]')?.setAttribute("content", service.meta);
    }
    return () => {
      document.title = "JIYONOX | AI, Software Engineering & Intelligent Automation";
    };
  }, [service]);

  if (!service) return <Navigate to="/" replace />;

  const idx = SERVICES.indexOf(service);
  const next = SERVICES[(idx + 1) % SERVICES.length];

  return (
    <div className="bg-jx-paper min-h-screen" data-testid={`service-page-${service.slug}`}>
      <header className="fixed top-0 inset-x-0 z-50 jx-glass border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-[76px] flex items-center justify-between">
          <Link to="/" data-testid="service-logo" className="flex items-center">
            <img src="/assets/logo-horizontal-sm.png" alt="JIYONOX" width="250" height="32" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="service-back-link" className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-jx-ink/60 hover:text-jx-ink transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to site
            </Link>
            <button
              data-testid="service-cta-button"
              onClick={() => setContactOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-jx-ink text-white text-sm font-medium px-5 py-2.5 transition-[transform,background-color] duration-300 hover:scale-[1.04] hover:bg-jx-purple active:scale-95"
            >
              Start a Conversation <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden pt-40 pb-20 lg:pt-48">
        <div className="pointer-events-none absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full opacity-25 blur-3xl jx-gradient-bg" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeUp>
            <p className="jx-overline text-jx-magenta flex items-center gap-3">
              <service.Icon className="w-4 h-4" /> {service.name} — JIYONOX Services
            </p>
          </FadeUp>
          <h1 className="mt-6 font-display font-extrabold tracking-tighter leading-[1.0] text-4xl sm:text-6xl lg:text-7xl text-jx-ink max-w-4xl" data-testid="service-title">
            <MaskedLine immediate delay={0.1}>{service.title}</MaskedLine>
          </h1>
          <FadeUp delay={0.25}>
            <p className="mt-6 max-w-2xl text-lg sm:text-xl leading-relaxed text-jx-ink/60 font-medium" data-testid="service-tagline">{service.tagline}</p>
          </FadeUp>
        </div>
      </section>

      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-6">
            {service.paragraphs.map((p, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <p className="text-base sm:text-lg leading-relaxed text-jx-ink/70" data-testid={`service-paragraph-${i}`}>{p}</p>
              </FadeUp>
            ))}
          </div>
          <div className="lg:col-span-5">
            <FadeUp delay={0.15}>
              <div className="rounded-3xl bg-white border border-jx-ink/8 p-8" data-testid="service-capabilities">
                <p className="jx-overline text-jx-purple">Capabilities</p>
                <div className="mt-5 flex flex-col gap-2.5">
                  {service.capabilities.map((c, i) => (
                    <div key={c} className="flex items-center gap-3 text-sm text-jx-ink/75" data-testid={`service-cap-${i}`}>
                      <Check className="w-4 h-4 text-jx-copper shrink-0" strokeWidth={2.5} />
                      {c}
                    </div>
                  ))}
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeUp><p className="jx-overline text-jx-purple mb-8">What you get</p></FadeUp>
          <div className="grid sm:grid-cols-3 gap-4">
            {service.outcomes.map((o, i) => (
              <FadeUp key={o.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl bg-white border border-jx-ink/8 p-7 hover:border-jx-magenta/30 hover:-translate-y-1 transition-[border-color,transform] duration-300" data-testid={`service-outcome-${i}`}>
                  <p className="font-display font-bold text-lg tracking-tight text-jx-ink">{o.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-jx-ink/55">{o.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-jx-night text-white relative overflow-hidden">
        <div className="pointer-events-none absolute -bottom-32 left-1/3 w-[480px] h-[300px] rounded-full bg-jx-magenta/15 blur-[120px]" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-wrap items-center justify-between gap-8 relative">
          <div>
            <h2 className="font-display font-extrabold tracking-tighter text-3xl sm:text-4xl" data-testid="service-cta-heading">
              Ready to <span className="jx-gradient-text">{service.name.toLowerCase()}</span> with intelligence?
            </h2>
            <p className="mt-3 text-white/55 max-w-lg">Tell us what you are trying to build, improve or automate — we will turn it into a working solution.</p>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <button
              data-testid="service-bottom-cta"
              onClick={() => setContactOpen(true)}
              className="group inline-flex items-center gap-3 rounded-full jx-gradient-bg text-white font-medium px-7 py-4 shadow-[0_16px_40px_rgba(209,45,140,0.3)] transition-transform duration-300 hover:scale-[1.04] active:scale-95"
            >
              Start a Conversation
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <Link
              to={`/services/${next.slug}`}
              data-testid="service-next-link"
              className="group inline-flex items-center gap-2 text-sm font-medium text-white/60 hover:text-white transition-colors"
            >
              Next: {next.title}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}
